import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import axiosInstance from '../../axios/axiosAuthInterceptor';
import { trackEvent } from '../../analytics';
import './LicensePage.scss';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

type LicenseEnum = 'Basic' | 'SmallOrg';

const LicensePage: React.FC = () => {
    const history = useHistory();
    const { t } = useTranslation();
    const [loading, setLoading] = useState<string | null>(null);

    const isLoggedIn = !!localStorage.getItem('accessToken');

    const startCheckout = async (licenseType: LicenseEnum) => {
        if (loading) return;
        setLoading(licenseType);
        try {
            const { data: user } = await axiosInstance.get('/user/get');

            const res = await axiosInstance.post('/stripe/create-checkout-session', {
                licenseType,
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                orgName: user.orgName || undefined,
                usersTotal: licenseType === 'SmallOrg' ? 1 : undefined,
                userId: user.id,
                returnUrl: window.location.origin,
            });

            trackEvent('Signup', 'Account Created', licenseType);

            const { id, url } = res.data;
            const stripe = await stripePromise;

            if (stripe && id) {
                const { error } = await stripe.redirectToCheckout({ sessionId: id });
                if (!error) return;
                if (url) {
                    window.location.href = url;
                    return;
                }
                throw error;
            }

            if (url) {
                window.location.href = url;
                return;
            }
            throw new Error('Stripe redirect unavailable');
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? e?.message ?? 'Checkout failed');
        } finally {
            setLoading(null);
        }
    };

    const handlePlanClick = (plan: 'student' | 'basis' | 'small-org') => {
        if (plan === 'student') {
            history.push('/signup?plan=student');
            return;
        }

        if (isLoggedIn) {
            const licenseType: LicenseEnum = plan === 'small-org' ? 'SmallOrg' : 'Basic';
            startCheckout(licenseType);
        } else {
            history.push(`/signup?plan=${plan}`);
        }
    };

    return (
        <div className="license-page">
            <section className="tiers container">
                {/* Student */}
                <article className="tier-card">
                    <h3 className="tier-title">{t('pages.license.student.title')}</h3>
                    <p className="description">{t('pages.license.student.desc')}</p>
                    <p className="price">
                        <span className="amount">{t('pages.license.student.price.amount')}</span>
                        <span className="per">{t('pages.license.common.perYear')}</span>
                    </p>
                    <ul className="features">
                        <li>{t('pages.license.student.features.0')}</li>
                        <li>{t('pages.license.student.features.1')}</li>
                        <li>{t('pages.license.student.features.2')}</li>
                    </ul>
                    <button className="cta" onClick={() => handlePlanClick('student')}>
                        {t('pages.license.common.ctaStudent')}
                    </button>
                </article>

                {/* Basis */}
                <article className="tier-card">
                    <h3 className="tier-title">{t('pages.license.basic.title')}</h3>
                    <p className="description">{t('pages.license.basic.desc')}</p>
                    <p className="price">
                        <span className="amount">{t('pages.license.basic.price.amount')}</span>
                        <span className="per">{t('pages.license.common.perYear')}</span>
                    </p>
                    <ul className="features">
                        <li>{t('pages.license.basic.features.0')}</li>
                        <li>{t('pages.license.basic.features.1')}</li>
                        <li>{t('pages.license.basic.features.2')}</li>
                    </ul>
                    <button className="cta" disabled={loading === 'Basic'} onClick={() => handlePlanClick('basis')}>
                        {loading === 'Basic'
                            ? t('pages.common.loading', { defaultValue: 'Laden...' })
                            : t('pages.license.common.ctaBasic')}
                    </button>
                </article>

                {/* Small org */}
                <article className="tier-card">
                    <h3 className="tier-title">{t('pages.license.smallOrg.title')}</h3>
                    <p className="sub">{t('pages.license.smallOrg.sub')}</p>
                    <p className="description orgDescription">{t('pages.license.smallOrg.desc')}</p>

                    <div className="org-price">
                        <div className="org-price-header">
                            <div className="amount">{t('pages.license.smallOrg.price.base')}</div>
                            <div className="per">{t('pages.license.common.perYear')}</div>
                        </div>
                        <div className="plus">+</div>
                        <div className="org-price-header">
                            <div className="amount">{t('pages.license.smallOrg.price.extra')}</div>
                            <div className="per">{t('pages.license.smallOrg.price.extraPer')}</div>
                        </div>
                    </div>

                    <ul className="features">
                        <li>{t('pages.license.smallOrg.features.0')}</li>
                        <li>{t('pages.license.smallOrg.features.1')}</li>
                        <li>{t('pages.license.smallOrg.features.2')}</li>
                    </ul>
                    <button
                        className="cta"
                        disabled={loading === 'SmallOrg'}
                        onClick={() => handlePlanClick('small-org')}
                    >
                        {loading === 'SmallOrg'
                            ? t('pages.common.loading', { defaultValue: 'Laden...' })
                            : t('pages.license.common.cta')}
                    </button>
                </article>
            </section>

            {/* Enterprise */}
            <section className="enterprise container">
                <article className="enterprise-card">
                    <div className="enterprise-header">
                        <h3>
                            <Trans i18nKey="pages.license.enterprise.title" />
                        </h3>
                        <br></br>
                    </div>

                    <div className="enterprise-grid">
                        <div className="contact">
                            <p>{t('pages.license.enterprise.desc')}</p>
                            <h4>{t('pages.license.enterprise.contactTitle')}</h4>
                            <address>
                                Schouwburgring 659
                                <br />
                                5038TX Tilburg
                                <br />
                                <a href="mailto:info@lifesight.me">info@lifesight.me</a>
                                <br />
                                0654 378 368
                                <br />
                                KvK 83985360
                                <br />
                                Btw-id NL863058978B01
                            </address>
                        </div>

                        <form
                            className="contact-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                            }}
                        >
                            <label>
                                {t('pages.license.enterprise.form.org')}
                                <input type="text" placeholder={t('pages.license.enterprise.form.orgPh')} required />
                            </label>

                            <label>
                                {t('pages.license.enterprise.form.email')}
                                <input type="email" placeholder={t('pages.license.enterprise.form.emailPh')} required />
                            </label>

                            <label>
                                {t('pages.license.enterprise.form.phone')}
                                <input type="tel" placeholder={t('pages.license.enterprise.form.phonePh')} required />
                            </label>

                            <label>
                                {t('pages.license.enterprise.form.message')}
                                <textarea placeholder={t('pages.license.enterprise.form.messagePh')} rows={2} />
                            </label>

                            <button className="cta small" type="submit">
                                {t('pages.license.enterprise.form.submit')}
                            </button>
                        </form>
                    </div>
                </article>
            </section>
        </div>
    );
};

export default LicensePage;
